import React, { useState, useEffect, useMemo, useSyncExternalStore, useDebugValue } from 'react';
import { Modal, Button, Form, Table, Dropdown } from 'react-bootstrap';
import Select from '../store/ThemedSelect';
import { createHashRouter, useAsyncError, useNavigate } from 'react-router-dom';
import { useAuth } from '../store/AuthContext';
import { useSchoolYear } from '../store/SchoolYearCalculator';

import { backendURL, backendWS } from '../IPaddress';
import '../CSS/CRUDfiles.css';
import 'bootstrap-icons/font/bootstrap-icons.css';
import {useUserData} from '../store/UserDataContext';
import ErrorModalMessage from '../store/ErrorMessage';
import {
  SearchInput,
  FilterSelect,
  ResultSummary,
  useListControls,
} from '../components/crud';

// Combined school + teacher name search text for a classroom row.
const CLASSROOM_SEARCH = (classroom, ctx) => {
  const schoolName = ctx.getSchoolName(classroom.school_id) || '';
  const teacher = ctx.teacherById.get(classroom.teacher_profile_id);
  const teacherName = teacher ? `${teacher.first_name} ${teacher.last_name}` : '';
  return `${schoolName} ${teacherName}`;
};

// Mentor filter (default: the logged-in user's own workload — seeded by an
// effect after the hook mounts). 'none' surfaces classrooms missing any mentor.
const CLASSROOM_FILTERS = [
  {
    key: 'mentor',
    label: 'Filter by Mentor',
    allOption: { value: '', label: 'All Mentors' },
    options: (items, ctx) => [
      { value: 'none', label: 'No Mentor Assigned' },
      ...(ctx?.mentors ?? []).map((mentor) => ({
        value: String(mentor.user_id),
        label: `${mentor.first_name} ${mentor.last_name}`,
      })),
    ],
    predicate: (classroom, value) => {
      if (value === 'none') {
        return (
          !classroom.drama_mentor_id ||
          !classroom.art_mentor_id ||
          !classroom.music_mentor_id
        );
      }
      const mentorId = parseInt(value, 10);
      return (
        classroom.drama_mentor_id === mentorId ||
        classroom.art_mentor_id === mentorId ||
        classroom.music_mentor_id === mentorId
      );
    },
  },
  {
    // The C. OBC Performance report counts each classroom school's student
    // body; schools missing that count silently contribute 0, so surface them.
    key: 'studentBody',
    label: 'Student Body Count',
    allOption: { value: '', label: 'All' },
    options: [
      { value: 'missing', label: 'Missing student body count' },
      { value: 'recorded', label: 'Has student body count' },
    ],
    predicate: (classroom, value, ctx) => {
      const school = ctx.schoolById?.get(classroom.school_id);
      const missing = !school || !school.student_body_count;
      return value === 'missing' ? missing : !missing;
    },
  },
];

const ClassroomComponent = () => {
  const [classrooms, setClassrooms] = useState([]);
  const [currentClassroom, setCurrentClassroom] = useState({
    classroom_id: '',
    school_id: '',
    school_name: '',        // comes from school table
    district_id: '',
    district_name: '',      // comes from district table
    teacher_id: '',         // still included if you’re joining users directly
    teacher_profile_id: null,
    drama_mentor_id: '',
    art_mentor_id: '',
    music_mentor_id: '',
    grade_id: '',
    status_id: '',          // from classroom.status_id
    service_type_id: '',
    school_year_id: '',     // was already in your DB but missing in your state
    size: null,
    created_date: '',       // new column from classroom
    notes: '',
    teacher_name: '',
  });

  const [showForm, setShowForm] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [classroomToDelete, setClassroomToDelete] = useState(null);
  const { authState } = useAuth();
  const { userData } = useUserData();
  const navigate = useNavigate();
  const [ mentors, setMentors ] = useState([]);
  const [ teachers, setTeachers ] = useState([]);
  // const [selectedTeacherName, setSelectedTeacherName] = useState('');
  const [dramaMentorSearch, setDramaMentorSearch] = useState("");
  const [artMentorSearch, setArtMentorSearch] = useState("");
  const [musicMentorSearch, setMusicMentorSearch] = useState("");
  const [selectedTeacher, setSelectedTeacher] = useState(null);
  const [selectedDramaMentor, setSelectedDramaMentor] = useState(null);
  const [selectedArtMentor, setSelectedArtMentor] = useState(null);
  const [selectedMusicMentor, setSelectedMusicMentor] = useState(null);
  const [ grades, setGrades ] = useState([]);
  const [ineligibleGradeModal, setIneligibleGradeModal] = useState(false);
  const [disableSubmit, setDisableSubmit] = useState(false);
  const [ schools, setSchools] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [selectedSchool, setSelectedSchool] = useState(null);
  const [schoolStudentBody, setSchoolStudentBody] = useState(null);
  const [homeSchoolModal, setHomeSchoolModal] = useState(false);
  const [privateSchoolModal, setPrivateSchoolModal] = useState(false);
  const [eventsPrograms, setEventsPrograms] = useState([]);
  const [ eventTypes, setEventTypes] = useState([]);
  const [ eventDates, setEventDates ] = useState([]);
  const [currentEventDate, setCurrentEventDate ] = useState({
    event_dates_id: '',
    event_id: '', 
    date: '', 
    start_time: '', 
    end_time: '',
    rfv_id: '',
    // mentor_id: ''
  })
    const [currentPerformanceDate, setCurrentPerformanceDate ] = useState({
    event_dates_id: '',
    event_id: '', 
    date: '', 
    start_time: '', 
    end_time: '',
  })
  const [isEditingClassroom, setIsEditingClassroom] = useState(false);
  const [isEditingNotesClassroom, setIsEditingNotesClassroom] = useState(false);
  const [showNewVisitModal, setShowNewVisitModal] = useState(false);
  const [ teacherStatuses, setTeacherStatuses] = useState([]);
  const [ errorMessage, setErrorMessage] = useState(null);
  const [ teacherProfiles, setTeacherProfiles] = useState([]);
  const [ isEditingVisit, setIsEditingVisit ] = useState(false);
  const [ isEditingPerformance, setIsEditingPerformance ] = useState(false);

  const [ classroomStatuses, setClassroomStatuses ] = useState([]);
  const [expandedClassroomId, setExpandedClassroomId] = useState(null);
  const [ classroomServiceTypes, setClassroomServiceTypes ] = useState([]);
  const [schoolYearList, setSchoolYearList] = useState([]);
  const [selectedSchoolYear, setSelectedSchoolYear] = useState();
  const {currentYear, schoolYearListParent} = useSchoolYear();
  const [groupByKey, setGroupByKey] = useState("district_name"); // default grouping
  const [hiddenWorkLoad, setHiddenWorkLoad ] = useState(false);
  const [expandedRows, setExpandedRows] = useState({});
  const [expandedScheduleSections, setExpandedScheduleSections] = useState({});
  const [classroomEvents , setClassroomEvents] = useState([]);
  const [classroomEventProgress, setClassroomEventProgress ] = useState([]);
  const [ classroomProgress, setClassroomProgress] = useState();
  const [ showNewPerformanceModal, setShowNewPerformanceModal] = useState(false); // to toggle between visits and performances

  const isAdmin = userData?.type_id === 5;

  // Authentication check
  useEffect(() => {
    if (!authState) {
      
      setErrorMessage("Looks like you will need to log back in.");
      navigate('/login');
    }
    if(userData.type_id === 5){
      setHiddenWorkLoad(true);
    }
  }, [authState, navigate, userData]);

  useEffect(() => {
    if (currentYear && schoolYearListParent.length) {
      setSelectedSchoolYear(currentYear);
      setSchoolYearList(schoolYearListParent);
    }
  }, [currentYear, schoolYearListParent]);
  
  useEffect(() => {
    if (selectedSchoolYear) {
      fetchClassrooms();
      fetchEventsPrograms();
      fetchEventDates();
      fetchClassroomProgress();
    }
  }, [selectedSchoolYear]);
  
  // Initial fetch for static or less frequently changed data
  useEffect(() => {
    fetchUsers();
    fetchTeacherProfiles();
    fetchGrades();
    fetchSchools();
    fetchDistricts();
    fetchEventTypes();
    fetchTeacherStatuses();
    fetchClassroomStatuses();
    fetchClassroomServiceTypes();
    fetchClassroomEvents();
    fetchClassroomEventProgress();
  }, []);

  useEffect(() => {
  const wsURL = `${backendURL.replace(/^http/, "ws")}/ws/classrooms`;
  const ws = new WebSocket(wsURL);

    ws.onopen = () => console.log("✅ Connected to Classroom WebSocket");

    ws.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data);

        const { type, entity } = message;
        if (!entity || !type) return;

        // ✅ Fetch whichever data changed
        switch (entity) {
          case "classroom":
            fetchClassrooms();
            fetchClassroomProgress();
            break;

          case "event_date":
            fetchEventDates();
            fetchClassroomProgress();
            break;

          default:
            console.log("Unrecognized entity:", entity);
        }

      } catch (err) {
        console.error("❌ WebSocket parse error:", err);
      }
    };

    ws.onclose = () => console.log("❌ Classroom WebSocket disconnected");

    return () => ws.close();
  }, [selectedSchoolYear]);


  

  const fetchSchools = async () => {
    // setLoading(prev => ({ ...prev, schools: true }));
    const response = await fetch(`${backendURL}/api/schools`);
    
    if (!response.ok) {
      setErrorMessage('Unable to retrieve school information');
    };
    const data = await response.json();
    
    if (Array.isArray(data)) {
      setSchools(data);

  } else {
      setErrorMessage('Fetched Schools data is not an array:', data);
  }} 
  const fetchTeacherProfiles = async() => {
    const response = await fetch(`${backendURL}/api/profiles`, {
      method:'GET',
      credentials: 'include'
    });
    if (!response.ok) {
      setErrorMessage('Unable to fetch teacher profiles.')
    }
    const data = await response.json();
    const filteredTeacherProfiles = data.filter( profile => profile.teacher_status === true);
    // console.log('teacher profiles: ', filteredTeacherProfiles)
    setTeacherProfiles(filteredTeacherProfiles)
  }
  const fetchClassroomStatuses = async () => {
    const response = await fetch(`${backendURL}/api/classroom-statuses`,{
      method: 'GET',
      credentials: 'include'
    })
    if (!response.ok){
      setErrorMessage('Error fetching the classroom statuses')
    }
    const data = await response.json();
    setClassroomStatuses(data);
  }

  const fetchClassroomEvents = async () => {
    const response = await fetch(`${backendURL}/api/classroom-events/`,{
      method: 'GET',
      credentials: 'include'
    })
    if (!response.ok){
      setErrorMessage('Error fetching the classroom events')
    }
    const data = await response.json();
    setClassroomEvents(data);
  }

    const fetchClassroomEventProgress = async () => {
    const response = await fetch(`${backendURL}/api/classroom-event-progress`,{
      method: 'GET',
      credentials: 'include'
    })
    if (!response.ok){
      setErrorMessage('Error fetching the classroom event progress')
    }
    const data = await response.json();
    setClassroomEventProgress(data);
  }

  const fetchDistricts = async () => {
    const url = `${backendURL}/api/districts`;
    const response = await fetch(url);
    if (response.ok) {
      const data = await response.json();
      setDistricts(data);
    } else {
      setErrorMessage('Unable to retrieve districts');
    }
  }
  const fetchClassroomServiceTypes = async () => {
    const response = await fetch(`${backendURL}/api/classroom-service-types`, {
      method: 'GET',
      credentials: 'include'
    })
    if (response.ok){
      const data = await response.json();
      setClassroomServiceTypes(data)
    } else {
      setErrorMessage('error fetching the classroom service types');
      return;
    }
  }

  const fetchEventsPrograms = async () => {
    try {const response = await fetch(`${backendURL}/api/events-programs/year/${selectedSchoolYear}`, {
      method: 'GET',
    //   credentials: 'include',
    });
    if (!response.ok){
      console.log(response)
    }
    const data = await response.json();
    setEventsPrograms(data);}
    catch {
      ErrorModalMessage('unable to fetch program events')
    }
  };

  const fetchTeacherStatuses = async () => {
    const response = await fetch(`${backendURL}/api/teacherstatuses`, {
      method: 'GET',
      credentials: 'include',
    });
    const data = await response.json();
    setTeacherStatuses(data);
  };

  const fetchEventTypes = async () => {
    const response = await fetch(`${backendURL}/api/event-types`, {
      method: 'GET',
      credentials: 'include'
    });
    try{
      const data = await response.json();
      setEventTypes(data);
    } catch (error) {
      setErrorMessage("Error fetching the event types: ", error.detail)
    }
  }

  const fetchEventDates = async () => {
    try {const response = await fetch(`${backendURL}/api/event-dates/year/${selectedSchoolYear}`, {
        method: 'GET',
        credentials: 'include'
    });
    const data = await response.json();
    const sortedData = data.sort((a, b) => new Date(a.date) - new Date(b.date));
      setEventDates(sortedData);
    }
    catch (error) {
        setErrorMessage("Error fetching event dates: ", error.detail);
    }
  }

  const fetchClassrooms = async () => {
    if (selectedSchoolYear) {
      try {
        const response = await fetch(`${backendURL}/api/classroom_group_view/${selectedSchoolYear}`, {
          method: 'GET',
          credentials: 'include',
        });
        if (!response.ok) {
          throw new Error('Error fetching the classrooms');
        }
        const data = await response.json();
        setClassrooms(data);
      } catch (error) {
        setErrorMessage(error.message);
      }
    } else {
      console.log("selected school year: ", selectedSchoolYear);
    }
  };

  const fetchGrades = async () => {
    const response = await fetch(`${backendURL}/api/grades`, {
      method: 'GET',
      credentials: 'include'
    });
    const data = await response.json();
    setGrades(data);
  };

  const fetchUsers = async () => {
    const response = await fetch(`${backendURL}/api/app-users`, {
      method: 'GET',
      credentials: 'include'
    });
    const data = await response.json();
    const teachers = data.filter(user => user.type_id === 1)
    const mentors = data.filter(user => user.type_id === 3 || user.type_id === 5)
    setMentors(mentors);
    setTeachers(teachers);
  };

  const fetchClassroomProgress = async () => {
    const res = await fetch(`${backendURL}/api/classroom-progress/${selectedSchoolYear}`,{
      method: 'GET',
      credentials: 'include'
    });
    const data = await res.json();
    setClassroomProgress(data);
  };

  const schoolOptions = schools.map(school_id => {
    const district = districts.find(d => d.district_id === school_id.district_id);
    const districtName = district ? district.name : 'Unknown District';
    
    return {
      label: `${school_id.name} (${districtName})`,
      value: school_id.school_id
    };
  });

  const handleSchoolChange = selectedOption => {
    setSelectedSchool(selectedOption);
    let schoolId = null;
    if (selectedOption) {
        schoolId = parseInt(selectedOption.value, 10);
    if (isNaN(schoolId)) {
        console.warn(`Warning: could not parse school_id value "${selectedOption.value}" as an integer`);
        schoolId = null;
        }}
        setCurrentClassroom({
        ...currentClassroom,
        school_id: schoolId,
        school_year_id: parseInt(selectedSchoolYear, 10),
        });

        const selectedSchoolObj = schools.find(school_id => school_id.school_id === schoolId);
        // Prefill the school's student body count so it can be reviewed/updated alongside the classroom.
        setSchoolStudentBody(selectedSchoolObj ? selectedSchoolObj.student_body_count : null);
    if (selectedOption && selectedOption.label.includes("(Private)")) {
        setPrivateSchoolModal(true);
        setDisableSubmit(true);
        // Optionally, you may clear the form and selections here
        setCurrentClassroom({
            size: null,
            grade_id: '',
            teacher_id: '',
            drama_mentor_id: '',
            art_mentor_id: '',
            music_mentor_id: '',
            school_id: '',
            teacher_profile_id: null,
            service_type_id: '',
            notes: '',
            teacher_name: '',
        });
        setSelectedSchool(null);
        setSchoolStudentBody(null);}
        // else if (selectedSchoolObj && selectedSchoolObj.name === "Home-School") {
        //   setHomeSchoolModal(true);
        //   setDisableSubmit(true);
        //   setCurrentClassroom({
        //       size: null, 
        //       grade_id: '', 
        //       teacher_id: '', 
        //       drama_mentor_id: '', 
        //       art_mentor_id: '', 
        //       music_mentor_id: '',
        //       school_id: '',
        //       teacher_profile_id: null,
        //       service_type_id: '',
        //   });
        //   setSelectedSchool(null);
        // }
         else {
        setDisableSubmit(false);
    }
};

const handleMentorSelect = (mentor, mentorType) => {
  setCurrentClassroom({
    ...currentClassroom,
    [`${mentorType}_id`]: mentor.user_id,
    school_year_id: parseInt(selectedSchoolYear, 10),
  });

  if (mentorType === 'drama_mentor') {
    setSelectedDramaMentor(mentor);
  } else if (mentorType === 'art_mentor') {
    setSelectedArtMentor(mentor);
  } else if (mentorType === 'music_mentor') {
    setSelectedMusicMentor(mentor);
  }
};

  const handleTeacherSelect = (teacher) => {
    setCurrentClassroom({
      ...currentClassroom,
      teacher_profile_id: teacher.profile_id,
      school_year_id: parseInt(selectedSchoolYear, 10),
    });
    setSelectedTeacher(teacher);
  };

  
const toggleRow = (classroomId) => {
  setExpandedRows(prev => ({
    ...prev,
    [classroomId]: !prev[classroomId],
  }));
};

  const toggleScheduleSection = (classroomId, sectionKey) => {
    const compositeKey = `${classroomId}-${sectionKey}`;
    setExpandedScheduleSections((prev) => ({
      ...prev,
      [compositeKey]: !prev[compositeKey],
    }));
  };

  const isScheduleSectionExpanded = (classroomId, sectionKey) =>
    expandedScheduleSections[`${classroomId}-${sectionKey}`] || false;


    
  // const filterTeachers = (searchTerm) => {
  //       return teachers.filter(teacher =>
  //         `${teacher.first_name} ${teacher.last_name}`.toLowerCase().includes(searchTerm.toLowerCase()))
  //     }

  // const handleFormChange = (e) => {
  //   setCurrentClassroom({ ...currentClassroom, [e.target.name]: e.target.value });
  // };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
  
    // Check if the form element should be an integer and convert it
    if (name === 'service_type_id' && value) {
      setCurrentClassroom(prevState => ({
        ...prevState,
        [name]: parseInt(value, 10), // Ensure value is treated as a base-10 integer
        school_year_id: parseInt(selectedSchoolYear, 10),
      }));
    } else {
      setCurrentClassroom(prevState => ({
        ...prevState,
        [name]: value,
        school_year_id: parseInt(selectedSchoolYear, 10),
      }));
    }
  };
  
  function handleIntegerChange(event) {
    const { name, value } = event.target;
    // Parse the input value as an integer
    const intValue = parseInt(value, 10);
    // Check for NaN values which can occur if the input is not a number
    // and decide how you want to handle it. Here, we fallback to 0 if NaN is encountered.
    // Alternatively, you could use '' or any other fallback value as needed.
    const validValue = isNaN(intValue) ? 0 : intValue;
  
    // Assuming you have a setState or similar function to update your state
    // For example, if you're managing form values in a state object named formData

    
    setCurrentClassroom({
      ...currentClassroom,
      [name]: validValue,
    });
  }

    function handleTextChange(event) {
    const { name, value } = event.target; 
    setCurrentClassroom({
      ...currentClassroom,
      [name]: value,
    });
  }
  const handleGradeChange = (e) => {
    const gradeValueStr = e.target.value;
    let gradeValue = parseInt(gradeValueStr, 10);
    
    // Check if the parsed value is NaN and handle accordingly
    if (isNaN(gradeValue)) {
        console.warn(`Warning: could not parse grade value "${gradeValueStr}" as an integer`);
        gradeValue = null; // or handle differently based on your use-case
    }
        // Find the selected grade object
        const selectedGrade = grades.find(grade => grade.grade_id === gradeValue);

    // Check if selected grade is one of the ineligible grades
    if (selectedGrade && ["College", "Community", "Preschool"].includes(selectedGrade.grade)) {
        setIneligibleGradeModal(true);
        setDisableSubmit(true);
        
        // setSelectedSchool(null);
        // setSelectedEventProgram(null);
        
    } else {
        setDisableSubmit(false);
        setCurrentClassroom({
            ...currentClassroom,
            grade_id: gradeValue
        });
    } 
};
const handleEventDateFormChange = (e) => {
  setCurrentEventDate({ ...currentEventDate, [e.target.name]: e.target.value });
};

const handlePerformanceDateFormChange = (e) => {
  setCurrentPerformanceDate({ ...currentPerformanceDate, [e.target.name]: e.target.value });
};
// const handleEventFormChange = (e) => {
//   console.log("being set at the handle")
//   setCurrentEvent({ ...currentEvent, [e.target.name]: e.target.value });
// };
const editEventDate = (eventDateId, classroom) => {
  setCurrentEventDate(eventDateId);
  setShowNewVisitModal(true);
  setIsEditingVisit(true);
  setCurrentClassroom(classroom);
  // Needs to set the current classroom here
};

const editPerformanceDate = (eventDateId) => {
  setCurrentPerformanceDate(eventDateId);
  setShowNewPerformanceModal(true);
  setIsEditingPerformance(true);
};

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!currentClassroom.teacher_profile_id) return setErrorMessage("Please select a teacher")
    if (!currentClassroom.school_id) return setErrorMessage("Please select a school")
    if (!currentClassroom.service_type_id) return setErrorMessage("Please select a service type")

    if (currentClassroom.classroom_id) {
      await updateClassroom(currentClassroom.classroom_id, currentClassroom);
    }
    if (!currentClassroom.classroom_id) {
      await createClassroom(currentClassroom);
    }

    // Persist the school's student body count if it changed, stamping the staleness timestamp.
    // Only PATCH on an actual change so we don't reset the timestamp needlessly.
    const schoolObj = schools.find(s => s.school_id === currentClassroom.school_id);
    const newCount = (schoolStudentBody === '' || schoolStudentBody === null)
      ? null
      : parseInt(schoolStudentBody, 10);
    if (currentClassroom.school_id && newCount !== null && !isNaN(newCount)
        && (!schoolObj || schoolObj.student_body_count !== newCount)) {
      await fetch(`${backendURL}/api/schools/${currentClassroom.school_id}/student-body`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ student_body_count: newCount }),
        credentials: 'include',
      });
      fetchSchools();
    }

    setSchoolStudentBody(null);
    setCurrentClassroom({
      size: null, 
      grade_id: '', 
      teacher_id: '', 
      drama_mentor_id: '', 
      art_mentor_id: '', 
      music_mentor_id: '',
      school_id: '',
      classroom_id: '',
      teacher_profile_id: null,
      service_type_id: '',
      notes: '',
      teacher_name: '',
    });
    setSelectedSchool(null);
    setSelectedArtMentor(null);
    setSelectedMusicMentor(null);
    setSelectedTeacher(null);
    setSelectedDramaMentor(null);
    setShowForm(false);
    setIsEditingClassroom(false);
    fetchClassrooms();
    fetchEventsPrograms();
    fetchClassroomStatuses();
  };

  const getGradeType = (gradeId) => {
    const grade = grades.find(g => g.grade_id === gradeId)
    if (grade) {
      return grade.grade
    }
    return "Unknown Grade";
  }

  const copyToClipboard = async (text, label = 'text') => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      setErrorMessage(`Unable to copy ${label}.`);
    }
  };

  const formatPhoneNumber = (phone) => {
    if (!phone) return '';
    const raw = String(phone).trim();
    const digits = raw.replace(/\D/g, '');

    if (digits.length === 10) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    }

    if (digits.length === 11 && digits.startsWith('1')) {
      return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
    }

    return raw;
  };

  const getTeacherDetails = (teacherId) => {
    const teacher = teacherProfiles.find(t => t.profile_id === teacherId);

    if (!teacher) return null;

    return (
      <div className="teacher-content-box">
        <div className="teacher-info-container">
          {/* Teacher's name */}
          <div className="teacher-contact-info-container">
            <span className='teacher-contact-info'>Teacher: </span>
            <span>{teacher.first_name} {teacher.last_name}</span>
          </div>

          {/* Teacher's phone numbers */}
          {teacher.home_phone_number && (
            <div className="teacher-contact-info-container">
              <span className='teacher-contact-info'>Home: </span>
              <span>{formatPhoneNumber(teacher.home_phone_number)}</span>
            </div>
          )}
          {teacher.cell_phone_number && (
            <div className="teacher-contact-info-container">
              <span className='teacher-contact-info'>Cell: </span>
              <span>{formatPhoneNumber(teacher.cell_phone_number)}</span>
            </div>
          )}
          {!teacher.home_phone_number && !teacher.cell_phone_number && (
            <div className="teacher-contact-info-container">
              <span className='teacher-contact-info'>No phone numbers added.</span>
            </div>
          )}

          {/* Teacher's email addresses */}
          {teacher.email && (
            <div className="teacher-contact-info-container">
              <span className='teacher-contact-info'>Email: </span>
              <span>{teacher.email}</span>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                className="ms-2 py-0 px-1"
                onClick={() => copyToClipboard(teacher.email, 'email')}
                aria-label="Copy email to clipboard"
              >
                <i className="bi bi-clipboard"></i>
              </Button>
            </div>
          )}
          {teacher.secondary_email && (
            <div className="teacher-contact-info-container">
              <span className='teacher-contact-info'>2nd Email: </span>
              <span>{teacher.secondary_email}</span>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                className="ms-2 py-0 px-1"
                onClick={() => copyToClipboard(teacher.secondary_email, 'secondary email')}
                aria-label="Copy secondary email to clipboard"
              >
                <i className="bi bi-clipboard"></i>
              </Button>
            </div>
          )}
        </div>
      </div>
    );
};
const getTeacherName = (teacherId) => {
    const teacher = teacherProfiles.find(t => t.profile_id === teacherId);

    if (!teacher) return null;

    return (
    <>
      {/* Teacher's name */}
      <span>{teacher.first_name} {teacher.last_name}</span>
    </>

    );
};
  const getTeacherStatus = (teacherId) => {
    const teacher = teacherProfiles.find(t => t.profile_id === teacherId)    
    if (teacher) {
      if (teacher.teacher_status_id === null){
        return `No status selected.`;
      } else{
        const teacherStatusObj = teacherStatuses.find(status => status.teacher_status_id === teacher.teacher_status_id);
        if (teacherStatusObj){
          return `${teacherStatusObj.status}`;
        }
      }      
    }
  }
  const getClassroomServiceType = (classroomServiceId) => {
    const serviceTypeObj = classroomServiceTypes.find(type => classroomServiceId === type.id)
    if (serviceTypeObj) {
      return (
        <span>{serviceTypeObj.service_name}</span>
      )
    } else {
      return (
        <span>{'Not Selected'}</span>
      )
    }
  }
  const getSchoolName = (schoolId) => {
    const school = schools.find(t => t.school_id === schoolId)
    if (school) {
      return `${school.name}`
    }
  }
  // True when the school's student body figure has never been recorded or hasn't
  // been refreshed in over a year (uses the dedicated student_body_count_updated_at column).
  const isStudentBodyStale = (schoolId) => {
    const school = schools.find(s => s.school_id === schoolId);
    if (!school) return false;
    const updatedAt = school.student_body_count_updated_at;
    if (!updatedAt) return true;
    const oneYearAgo = new Date();
    oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
    return new Date(updatedAt) < oneYearAgo;
  }
  const getSchoolPhone = (schoolId) => {
    const school = schools.find(t => t.school_id === schoolId)
    if (school) {
      return formatPhoneNumber(school.phone)
    }
  }
  const getSchoolAddress = (schoolId) => {
    const school = schools.find(t => t.school_id === schoolId)
    if (school) {
        let address = `${school.street} ${school.city} ${school.zip}`
        // if (address.includes(`P.O.`)) {
        //   return `Invalid Address: ${address}`
        // }
      return address
    }
  }
  const getClassroomEventName = (rfv_id) => {
    const eventName = classroomEvents.find(t => t.rfv_id=== rfv_id)
    if (eventName) {
      return ` ${eventName.reason_for_visit}`
  }
  }

    const filteredClassroomEventTypes = () => {
    return classroomEvents.filter(event =>
      // Match events assigned to this service type
      event.service_type_id === currentClassroom.service_type_id ||
      // OR allow global events (null)
      event.service_type_id === null
    );
  };

//   const updateClassroomStatus = (classroomId, updatedStatus) => {
//     setClassroomStatuses(prevStatuses => 
//         prevStatuses.map(status => 
//             status.classroom_id === classroomId ? updatedStatus : status
//         )
//     );
// };
//   const toggleClassroomExpansion = (classroomId) => {
//     setExpandedClassroomId(prevId => prevId === classroomId ? null : classroomId);
//   };
//   const sendStatusUpdate = async (statusObj, classroomStatusId, key, value) => {

//     const payload = {
//       ...statusObj,
//       id: classroomStatusId,
//         [key]: value
//     };

//     try {
//         const response = await fetch(`${backendURL}/api/classroom-statuses/${classroomStatusId}`, {
//             method: 'PUT',
//             headers: {
//                 'Content-Type': 'application/json',
//             },
//             credentials: "include",
//             body: JSON.stringify(payload),
//         });

//         if (!response.ok) {
//           setErrorMessage("There was an error updating the classroom data. Your changes likely didn't save");
//           return;
//         }

//         const data = await response.json();
//         // Handle success (e.g., showing a confirmation message)
//     } catch (error) {
//         setErrorMessage('Error:', String(error));
//         // Handle error (e.g., showing an error message)
//     }
//   };
  const ClassroomStatus = ({ classroom, classroomProgress, classroomEvents }) => {
    // Find the progress record for this classroom
    const progress = classroomProgress.find(p => p.classroom_id === (classroom.classroom_id));

    if (!progress) return <small>No progress data</small>;

    // Compare completed visit types against total defined in classroomEvents
    const eventLength =  
      classroomEvents.filter(event =>
        event.service_type_id === classroom.service_type_id ||
        event.service_type_id === null
      ).length;

    const completedCount = progress.visits.filter(v => v.latest_date !== null).length;

    // Compute percentage dynamically
    const completionPercentage =
      eventLength > 0 ? (completedCount / eventLength) * 100 : 0;

    return (
      <div className="p-2 border rounded bg-body-tertiary">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <h6 className="mb-0">Visits Progress</h6>
          <span>
            {completedCount}/{eventLength} ({Math.round(completionPercentage)}%) Complete
          </span>
        </div>

        {/* Progress Bar */}
        <div
          style={{
            width: "100%",
            backgroundColor: "var(--bs-secondary-bg)",
            borderRadius: "8px",
            height: "20px",
            marginBottom: "10px",
          }}
        >
          <div
            style={{
              width: `${completionPercentage}%`,
              backgroundColor:
                completionPercentage < 40
                  ? "#dc3545"
                  : completionPercentage < 80
                  ? "#ffc107"
                  : "#28a745",
              height: "20px",
              borderRadius: "8px",
            }}
          ></div>
        </div>

        {/* Completed Visits List */}
        <ul className="list-unstyled mb-0">
          {progress.visits
            .filter(v => v.latest_date)
            .map(v => (
              <li key={v.reason_for_visit}>
                <strong>{v.reason_for_visit}</strong>: {v.latest_date}
              </li>
            ))}
        </ul>

        {progress.visits.filter(v => v.latest_date).length === 0 && (
          <small className="text-muted">No visits completed yet.</small>
        )}
      </div>
    );
  };

  const ClassroomStatusCollapsed = ({
  classroom,
  classroomProgress = [],
  classroomEvents = [],
}) => {
  // Guard against undefined values (this line ensures .find() always runs safely)
  if (!Array.isArray(classroomProgress) || !Array.isArray(classroomEvents)) {
    return null;
  }

  const eventLength =  
      classroomEvents.filter(event =>
        event.service_type_id === classroom.service_type_id ||
        event.service_type_id === null
      ).length;

  // Find the progress entry for this classroom
  const progress = classroomProgress.find((p) => p.classroom_id === classroom.classroom_id);

  // Define totals dynamically
  const totalVisitTypes = classroomEvents.length || 0;
  const completedCount = progress
    ? progress.visits.filter((v) => v.latest_date !== null).length
    : 0;

  const completionPercentage =
    eventLength > 0 ? (completedCount / eventLength) * 100 : 0;

  return (
    <div className="d-flex align-items-center gap-2" style={{ width: "100%" }}>
      <span className="text-muted small flex-shrink-0">Visits Progress</span>

      {/* Progress Bar */}
      <div
        className="progress-bar-container-collapsed flex-grow-1"
        style={{
          height: "10px",
          backgroundColor: "var(--bs-tertiary-bg)",
          borderRadius: "8px",
          overflow: "hidden",
        }}
      >
        <div
          className="progress-fill-collapsed"
          style={{
            width: `${completionPercentage}%`,
            height: "100%",
            backgroundColor:
              completionPercentage < 40
                ? "#dc3545" // red
                : completionPercentage < 80
                ? "#ffc107" // yellow
                : "#28a745", // green
            transition: "width 0.4s ease-in-out",
          }}
        ></div>
      </div>

      <div style={{ minWidth: "60px", textAlign: "right" }}>
        <small>
          {completedCount}/{eventLength} ({Math.round(completionPercentage)}%)
        </small>
      </div>
    </div>
  );
};


  const getMentorName = (mentorId) => {
    const mentor = mentors.find(t => t.user_id === mentorId)
    if (mentor) {
      return `${mentor.first_name} ${mentor.last_name}`
    }
  }

  const getSortedEventDates = (eventId) =>
    eventDates
      .filter((eventDate) => eventDate.event_id === eventId)
      .sort((a, b) => new Date(`${a.date}T00:00:00`) - new Date(`${b.date}T00:00:00`));

  const getClassroomPrograms = (classroomId, eventTypeId) =>
    eventsPrograms.filter(
      (eventProgram) =>
        eventProgram.classroom_id === classroomId && eventProgram.event_type_id === eventTypeId
    );

  const getLastClassroomActivityLabel = (classroomId, eventTypeId) => {
    const eventProgramsList = getClassroomPrograms(classroomId, eventTypeId);

    if (!eventProgramsList.length) {
      return eventTypeId === 2 ? "No visit tracks yet" : "No performance tracks yet";
    }

    const latestEventDate = eventProgramsList
      .flatMap((eventProgram) =>
        getSortedEventDates(eventProgram.event_id).map((eventDate) => ({
          eventDate,
          eventProgram,
        }))
      )
      .sort(
        (a, b) =>
          new Date(`${b.eventDate.date}T00:00:00`) - new Date(`${a.eventDate.date}T00:00:00`)
      )[0];

    if (!latestEventDate) {
      return eventTypeId === 2 ? "No visits scheduled yet" : "No performances scheduled yet";
    }

    const latestVisitType = getClassroomEventName(latestEventDate.eventDate.rfv_id)?.trim();

    if (eventTypeId === 2) {
      if (latestVisitType) {
        return `${latestEventDate.eventProgram.name}: ${latestVisitType}`;
      }

      return latestEventDate.eventProgram.name;
    }

    return latestEventDate.eventProgram.name || "Performance";
  };
// const getWorkshopEvent = (teacherProfileId) => {
//     const profileObj = teacherProfiles.filter(profile => 
//       profile.profile_id === teacherProfileId)

//     if (profileObj.length > 0){
//       const eventProgramObj = eventsPrograms.filter(eventProgram =>
//        eventProgram.event_id === profileObj[0].workshop_event_id
//        );
//        return eventProgramObj.length > 0 ? (
//         <div className='mentor-events' >
//           {eventProgramObj.map(eventProgram => (
//             <div className='mentor-event-item' key={eventProgram.event_id} >
//               <Button onClick={() => toggleOpenState(eventProgram.event_id)}>
//                 {'Workshops'}
//               </Button>
//               <Collapse in={openStates[eventProgram.event_id] || false}>
//                 <div id={`event-dates-${eventProgram.event_id}`}>
//                   <Button onClick={() => handleCreateEventDate(eventProgram.event_id)}>
//                     New Workshop
//                   </Button>
//                   <div>{getEventDates(eventProgram.event_id)}</div>
//                 </div>
//               </Collapse>
//             </div>
//           ))}
//         </div>
//       ) : <p>No workshops found</p>;

//       }    
//   }
  const formatTime = (timeString) => {
    // Assuming timeString is in 'HH:MM:SS' format
    const [hours, minutes] = timeString.split(':');
    const hoursInt = parseInt(hours, 10);
    const suffix = hoursInt >= 12 ? 'PM' : 'AM';
    const formattedHours = ((hoursInt + 11) % 12 + 1); // Convert 24hr to 12hr format
    return `${formattedHours}:${minutes} ${suffix}`;
  };

  const formatDateShort = (dateString) => {
    if (!dateString) return "N/A";
    const parsedDate = new Date(`${dateString}T00:00:00`);
    if (Number.isNaN(parsedDate.getTime())) return dateString;
    return parsedDate.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  };

  const formatDateDetailed = (dateString) => {
    if (!dateString) return "N/A";
    const parsedDate = new Date(`${dateString}T00:00:00`);
    if (Number.isNaN(parsedDate.getTime())) return dateString;
    return parsedDate.toLocaleDateString(undefined, {
      weekday: "short",
      month: "short",
      day: "numeric",
    });
  };

  const getScheduledDatesSnapshot = (relatedDates) => {
    if (!relatedDates.length) {
      return { count: 0, nextDate: null, latestDate: null };
    }

    const sortedDates = [...relatedDates].sort((a, b) => new Date(a.date) - new Date(b.date));
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const nextDateObj = sortedDates.find((eventDate) => {
      const eventDateVal = new Date(`${eventDate.date}T00:00:00`);
      return eventDateVal >= today;
    }) || null;
    const latestDateObj = sortedDates[sortedDates.length - 1] || null;

    return {
      count: relatedDates.length,
      nextDate: nextDateObj?.date || null,
      latestDate: latestDateObj?.date || null,
    };
  };

  const getClassroomEventSummary = (classroomId, eventTypeId) => {
    const programIds = eventsPrograms
      .filter((eventProgram) =>
        eventProgram.classroom_id === classroomId && eventProgram.event_type_id === eventTypeId
      )
      .map((eventProgram) => eventProgram.event_id);

    if (!programIds.length) {
      return { count: 0, nextDate: null, latestDate: null };
    }

    const relatedDates = eventDates.filter((eventDate) => programIds.includes(eventDate.event_id));
    return getScheduledDatesSnapshot(relatedDates);
  };

  const getScheduleSectionSummary = (snapshot, scheduleType) => {
    if (!snapshot.count) {
      return scheduleType === "visit"
        ? "0 total | No visits scheduled"
        : "0 total | No performances scheduled";
    }

    return `${snapshot.count} total | Most recent: ${formatDateShort(snapshot.latestDate)}`;
  };

  const renderClassroomDateCards = (programDates, scheduleType, onSelect) => (
    <div className="classroom-schedule-card-grid">
      {programDates.map((eventDate) => {
        const visitType = getClassroomEventName(eventDate.rfv_id)?.trim();

        return (
          <button
            type="button"
            key={eventDate.event_dates_id}
            className="schedule-card classroom-schedule-card"
            onClick={() => onSelect(eventDate)}
          >
            <div className="schedule-card-header">
              <div className="schedule-card-title">{formatDateDetailed(eventDate.date)}</div>
              {scheduleType === "visit" && visitType ? (
                <span className="classroom-schedule-badge">{visitType}</span>
              ) : null}
            </div>
            <div className="schedule-card-time">
              {formatTime(eventDate.start_time)} to {formatTime(eventDate.end_time)}
            </div>
            <div className="classroom-schedule-card-action">
              Edit {scheduleType === "visit" ? "visit" : "performance"}
            </div>
          </button>
        );
      })}
    </div>
  );

  const getProgramSummaryMeta = (programDates, scheduleType, emptyScheduleMessage) => {
    if (!programDates.length) {
      return {
        primary: emptyScheduleMessage,
        secondary: null,
      };
    }

    const snapshot = getScheduledDatesSnapshot(programDates);
    const latestEventDate = programDates[programDates.length - 1];
    const latestVisitType = getClassroomEventName(latestEventDate?.rfv_id)?.trim();

    if (scheduleType === "visit") {
      const primary = snapshot.latestDate
        ? `Last visit: ${latestVisitType || "Visit"} on ${formatDateShort(snapshot.latestDate)}`
        : emptyScheduleMessage;
      const secondary =
        snapshot.nextDate && snapshot.nextDate !== snapshot.latestDate
          ? `Next scheduled: ${formatDateShort(snapshot.nextDate)}`
          : null;

      return { primary, secondary };
    }

    const primary = snapshot.nextDate
      ? `Next performance: ${formatDateShort(snapshot.nextDate)}`
      : snapshot.latestDate
        ? `Most recent: ${formatDateShort(snapshot.latestDate)}`
        : emptyScheduleMessage;
    const secondary =
      snapshot.latestDate && snapshot.latestDate !== snapshot.nextDate
        ? `Most recent: ${formatDateShort(snapshot.latestDate)}`
        : null;

    return { primary, secondary };
  };

  const renderClassroomProgramCards = ({
    eventProgramsList,
    classroom,
    scheduleType,
    emptyState,
    emptyScheduleMessage,
    createLabel,
    onCreate,
    onSelect,
  }) => {
    if (!eventProgramsList.length) {
      return <p>{emptyState}</p>;
    }

    return (
      <div className="classroom-schedule-program-grid">
        {eventProgramsList.map((eventProgram) => {
          const programDates = getSortedEventDates(eventProgram.event_id);
          const summaryMeta = getProgramSummaryMeta(programDates, scheduleType, emptyScheduleMessage);

          return (
            <section className="classroom-schedule-program-card" key={eventProgram.event_id}>
              <div className="classroom-schedule-program-header">
                <div className="classroom-schedule-program-copy">
                  <div className="classroom-schedule-program-title-row">
                    <div className="classroom-schedule-program-title">{eventProgram.name}</div>
                    <span className="classroom-schedule-program-count">
                      {programDates.length} scheduled
                    </span>
                  </div>
                  <div className="classroom-schedule-program-meta">{summaryMeta.primary}</div>
                  {summaryMeta.secondary ? (
                    <div className="classroom-schedule-program-meta classroom-schedule-program-meta-secondary">
                      {summaryMeta.secondary}
                    </div>
                  ) : null}
                </div>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => onCreate(eventProgram.event_id, classroom)}
                >
                  {createLabel}
                </Button>
              </div>
              {programDates.length ? (
                renderClassroomDateCards(programDates, scheduleType, (eventDate) => onSelect(eventDate, classroom))
              ) : (
                <div className="schedule-empty-state classroom-schedule-empty">
                  {emptyScheduleMessage}
                </div>
              )}
            </section>
          );
        })}
      </div>
    );
  };

  const renderClassroomScheduleSection = ({
    classroomId,
    sectionKey,
    title,
    summary,
    preview,
    children,
  }) => {
    const isOpen = isScheduleSectionExpanded(classroomId, sectionKey);

    return (
      <section className={`classroom-schedule-section ${isOpen ? "is-open" : ""}`}>
        <button
          type="button"
          className="classroom-schedule-toggle"
          onClick={() => toggleScheduleSection(classroomId, sectionKey)}
        >
          <div className="classroom-schedule-toggle-copy">
            <div className="classroom-schedule-toggle-title">{title}</div>
            <div className="classroom-schedule-toggle-subtitle">{summary}</div>
            <div className="classroom-schedule-toggle-preview">{preview}</div>
          </div>
          <div className="classroom-schedule-toggle-action">
            <span>{isOpen ? "Hide details" : "Show details"}</span>
            <i className={`bi ${isOpen ? "bi-chevron-up" : "bi-chevron-down"}`}></i>
          </div>
        </button>
        {isOpen ? <div className="classroom-schedule-section-body">{children}</div> : null}
      </section>
    );
  };

  // TODO 2026-03-13: if this schedule view evolves again, keep classroom visits on a
  // classroom-specific pattern. The event-program card layout should be adapted, not copied.
  const getEventProgram = (classroom) => {
    try {
      if (eventsPrograms) {
        const eventProgramObj = getClassroomPrograms(classroom.classroom_id, 2);

        return renderClassroomProgramCards({
          eventProgramsList: eventProgramObj,
          classroom,
          scheduleType: "visit",
          emptyState: "No visits found",
          emptyScheduleMessage: "No visits scheduled yet",
          createLabel: "New visit",
          onCreate: (eventId, currentClassroom) => handleCreateEventDate(eventId, currentClassroom),
          onSelect: (eventDate, currentClassroom) => editEventDate(eventDate, currentClassroom),
        });
      }
    } catch {
      ErrorModalMessage("error filtering the event programs related to classrooms");
    }
  };

  const getPerformanceEvent = (classroomId) => {
    const eventProgramObjs = getClassroomPrograms(classroomId, 1);

    return renderClassroomProgramCards({
      eventProgramsList: eventProgramObjs,
      classroom: null,
      scheduleType: "performance",
      emptyState: "No performances found",
      emptyScheduleMessage: "No performances scheduled yet",
      createLabel: "New performance",
      onCreate: (eventId) => handleCreatePerformanceDate(eventId),
      onSelect: (eventDate) => editPerformanceDate(eventDate),
    });
  };
  const createClassroom = async (classroomData) => {

    await fetch(`${backendURL}/api/classrooms`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(classroomData),
      credentials: 'include'
    });
  };
  const updateClassroom = async (classroomId, classroomData) => {

    await fetch(`${backendURL}/api/classrooms/${classroomId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(classroomData),
      credentials: 'include'
    });


    setCurrentClassroom({
      size: null, 
      grade_id: '', 
      teacher_id: '', 
      drama_mentor_id: '', 
      art_mentor_id: '', 
      music_mentor_id: '',
      school_id: '',
      classroom_id: '',
      teacher_profile_id: null,
      service_type_id: '',
      notes: '',
      teacher_name: '',
    });
  };

  const updateClassroomMentorInline = async (classroom, mentorField, mentorId) => {
    if (!isAdmin) {
      setErrorMessage('Only admins can update mentor assignments.');
      return;
    }

    try {
      const mentorValue = mentorId ? parseInt(mentorId, 10) : null;
      const payload = {
        ...classroom,
        [mentorField]: mentorValue,
        school_year_id: parseInt(selectedSchoolYear, 10),
      };

      const response = await fetch(`${backendURL}/api/classrooms/${classroom.classroom_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        credentials: 'include',
      });

      if (!response.ok) {
        setErrorMessage('Unable to update mentor assignment.');
        return;
      }

      setClassrooms((prev) =>
        prev.map((item) =>
          item.classroom_id === classroom.classroom_id
            ? { ...item, [mentorField]: mentorValue }
            : item
        )
      );
    } catch {
      setErrorMessage('Unable to update mentor assignment.');
    }
  };
  const deleteClassroom = async () => {
    try {
      if (classroomToDelete) {
      const response = await fetch(`${backendURL}/api/classrooms/${classroomToDelete}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      if (!response.ok) {
        setErrorMessage(
          'Unable to delete this classroom. Check to see if one of the events has a date and delete that first. Then try deleting the classroom again.');
        setShowModal(false); 
        return;
      }
      
      setClassroomToDelete(null);
      setShowModal(false);
      fetchClassrooms();
      }
    } catch (error) {
      setErrorMessage(
        'Unable to delete this classroom. Check to see if one of the events has a date and delete that first. Then try deleting the classroom again.');
      setShowModal(false); 
      return;
    }
  };
  const editClassroom = (classroom) => {
    // Set the current classroom state with the selected classroom data
    setIsEditingClassroom(true);
    setCurrentClassroom({
        size: classroom.size,
        grade_id: classroom.grade_id,
        teacher_id: classroom.teacher_id,
        drama_mentor_id: classroom.drama_mentor_id,
        art_mentor_id: classroom.art_mentor_id,
        music_mentor_id: classroom.music_mentor_id,
        school_id: classroom.school_id,
        classroom_id: classroom.classroom_id,
        teacher_profile_id: classroom.teacher_profile_id,
        service_type_id: classroom.service_type_id,
        notes: classroom.notes,
        teacher_name: classroom.teacher_name,
    });
    const schoolObject = schoolOptions.find(school => school.value === classroom.school_id);
        
    const dramaMentorObj = mentors.find(mentor => mentor.user_id === classroom.drama_mentor_id);

    const artMentorObj = mentors.find(mentor => mentor.user_id === classroom.art_mentor_id);
    
    const musicMentorObj = mentors.find(mentor => mentor.user_id === classroom.music_mentor_id);
    
    const teacherObj = teacherProfiles.find(teacher => teacher.profile_id === classroom.teacher_profile_id);
    

    const schoolForBody = schools.find(s => s.school_id === classroom.school_id);
    setSchoolStudentBody(schoolForBody ? schoolForBody.student_body_count : null);

    setSelectedSchool(schoolObject);
    setSelectedArtMentor(artMentorObj);
    setSelectedDramaMentor(dramaMentorObj);
    setSelectedMusicMentor(musicMentorObj);
    setSelectedTeacher(teacherObj);
    setShowForm(true);
};
  const editNotesClassroom = (classroom) => {
      // Set the current classroom state with the selected classroom data
      setIsEditingNotesClassroom(true);
      setCurrentClassroom({
          size: classroom.size,
          grade_id: classroom.grade_id,
          teacher_id: classroom.teacher_id,
          drama_mentor_id: classroom.drama_mentor_id,
          art_mentor_id: classroom.art_mentor_id,
          music_mentor_id: classroom.music_mentor_id,
          school_id: classroom.school_id,
          classroom_id: classroom.classroom_id,
          teacher_profile_id: classroom.teacher_profile_id,
          service_type_id: classroom.service_type_id,
          notes: classroom.notes,
          teacher_name: classroom.teacher_name,
      });
  };

  const handleCreateEventDate = (eventId, classroom) => {
    setCurrentEventDate({
      ...currentEventDate,
      event_id: eventId,
      rfv_id: '',
      school_year_id: parseInt(selectedSchoolYear, 10),
    })
    setShowNewVisitModal(true)
    setCurrentClassroom(classroom);
  }

    const handleCreatePerformanceDate = (eventId) => {
    setCurrentPerformanceDate({
      ...currentPerformanceDate,
      event_id: eventId,
      school_year_id: parseInt(selectedSchoolYear, 10),
    })
    setShowNewPerformanceModal(true)
  }
  
    const handlePerformanceSubmit = async (e) => {
  e.preventDefault();

  // --- Validation ---
  if (!currentPerformanceDate.date) return setErrorMessage("Please include a date");
  if (!currentPerformanceDate.start_time) return setErrorMessage("Please include a start time");
  if (!currentPerformanceDate.end_time) return setErrorMessage("Please include an end time");
  if (currentPerformanceDate.start_time > currentPerformanceDate.end_time)
    return setErrorMessage("The start time must begin before the end time");

  const editing = Boolean(currentPerformanceDate.event_dates_id);
  const method = editing ? "PUT" : "POST";
  const url = editing
    ? `${backendURL}/api/event-dates/${currentPerformanceDate.event_dates_id}`
    : `${backendURL}/api/event-dates`;

  try {
    const res = await fetch(url, {
      method,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(currentPerformanceDate),
      credentials: "include",
    });

    if (!res.ok) {
      const errData = await res.json().catch(() => ({}));
      console.error("Error saving visit:", errData);
      return setErrorMessage("There was an error saving the visit.");
    }

    // ✅ Wait for the backend to respond with the saved record
    const savedRecord = await res.json().catch(() => null);

    // ✅ Update local state (replace, don't append)
    if (savedRecord && savedRecord.event_dates_id) {
      setEventDates((prev) => {
        // filter out any previous instance of the same event_dates_id
        const filtered = prev.filter(
          (ed) => ed.event_dates_id !== savedRecord.event_dates_id
        );
        return [...filtered, savedRecord].sort(
          (a, b) => new Date(a.date) - new Date(b.date)
        );
      });
    } else {
      // fallback: re-fetch if response doesn’t return the record
      await fetchEventDates();
    }

    // ✅ Reset UI
    setCurrentPerformanceDate({
      event_dates_id: "",
      event_id: "",
      date: "",
      start_time: "",
      end_time: "",
    });
    setShowNewPerformanceModal(false);
    setShowForm(false);
  } catch (err) {
    console.error("Unexpected error saving performance:", err);
    setErrorMessage("Unexpected error saving the performance.");
  }
    fetchEventDates();
    fetchClassroomProgress();
};

  const handleVisitSubmit = async (e) => {
  e.preventDefault();

  // --- Validation ---
  if (!currentEventDate.date) return setErrorMessage("Please include a date");
  if (!currentEventDate.start_time) return setErrorMessage("Please include a start time");
  if (!currentEventDate.end_time) return setErrorMessage("Please include an end time");
  if (currentEventDate.start_time > currentEventDate.end_time)
    return setErrorMessage("The start time must begin before the end time");
  if (!currentEventDate.rfv_id) return setErrorMessage("Please select a visit type");

  const editing = Boolean(currentEventDate.event_dates_id);
  const method = editing ? "PUT" : "POST";
  const url = editing
    ? `${backendURL}/api/event-dates/${currentEventDate.event_dates_id}`
    : `${backendURL}/api/event-dates`;

  try {
    const res = await fetch(url, {
      method,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(currentEventDate),
      credentials: "include",
    });

    if (!res.ok) {
      const errData = await res.json().catch(() => ({}));
      console.error("Error saving visit:", errData);
      return setErrorMessage("There was an error saving the visit.");
    }

    // ✅ Wait for the backend to respond with the saved record
    const savedRecord = await res.json().catch(() => null);

    // ✅ Update local state (replace, don't append)
    if (savedRecord && savedRecord.event_dates_id) {
      setEventDates((prev) => {
        // filter out any previous instance of the same event_dates_id
        const filtered = prev.filter(
          (ed) => ed.event_dates_id !== savedRecord.event_dates_id
        );
        return [...filtered, savedRecord].sort(
          (a, b) => new Date(a.date) - new Date(b.date)
        );
      });
    } else {
      // fallback: re-fetch if response doesn’t return the record
      await fetchEventDates();
    }

    // ✅ Reset UI
    setCurrentEventDate({
      event_dates_id: "",
      event_id: "",
      date: "",
      start_time: "",
      end_time: "",
      rfv_id: "",
    });
    setShowNewVisitModal(false);
    setShowForm(false);
  } catch (err) {
    console.error("Unexpected error saving visit:", err);
    setErrorMessage("Unexpected error saving the visit.");
  }
    fetchEventDates();
    fetchClassroomProgress();
};

    const deleteEventDate = async (eventDateId) => {

      await fetch(`${backendURL}/api/event-dates/${eventDateId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      setShowNewVisitModal(false);
      setCurrentEventDate({
        event_dates_id: '',
        event_id: '', 
        date: '', 
        start_time: '', 
        end_time: '',
        rfv_id: '',
        // mentor_id: ''
      })
      fetchEventDates();    
      fetchClassroomProgress();
    };

    const deletePerformanceDate = async (eventDateId) => {

      await fetch(`${backendURL}/api/event-dates/${eventDateId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      setShowNewPerformanceModal(false);
      setCurrentPerformanceDate({
        event_dates_id: '',
        event_id: '', 
        date: '', 
        start_time: '', 
        end_time: '',
      })
      fetchEventDates();    
      fetchClassroomProgress();
    };
  const handleCancel = () => {
    setCurrentClassroom({/*setting back to initial state */})
    setShowForm(false);
    setIsEditingClassroom(false);
    setSelectedSchool(null);
    setSelectedArtMentor(null);
    setSelectedMusicMentor(null);
    setSelectedTeacher(null);
    setSelectedDramaMentor(null);
    setCurrentEventDate({});
    setCurrentPerformanceDate({});
    setShowNewVisitModal(false);
    setIsEditingVisit(false);
    setIsEditingPerformance(false);
    setCurrentClassroom({
        size: null, 
        grade_id: '', 
        teacher_id: '', 
        drama_mentor_id: '', 
        art_mentor_id: '', 
        music_mentor_id: '',
        teacher_profile_id: null,
        service_type_id: '',
        notes: '',
        teacher_name: '',
      });
    setDramaMentorSearch('');
    setArtMentorSearch('');
    setMusicMentorSearch('');
    setShowNewPerformanceModal(false);
    setIsEditingNotesClassroom(false);
  };
  const handleYearChange = (event) => {
    setSelectedSchoolYear(event.target.value);
  }; 

  
  // Stable context for the hook: teacher lookups via Map instead of a
  // per-classroom .find, and only rebuilt when the underlying data changes.
  const listContext = useMemo(
    () => ({
      getSchoolName,
      teacherById: new Map(teacherProfiles.map((t) => [t.profile_id, t])),
      schoolById: new Map(schools.map((s) => [s.school_id, s])),
      mentors,
    }),
    // getSchoolName closes over `schools`, so key the memo on it.
    [schools, teacherProfiles, mentors] // eslint-disable-line react-hooks/exhaustive-deps
  );

  // Search AND the mentor filter both run through the shared hook, so the
  // result count, isFiltered, and Clear all reflect the mentor selection.
  // Grouping below stays feature-local and consumes the hook's output.
  const {
    items: processedClassrooms,
    resultCount,
    totalCount,
    isFiltered,
    search,
    filterList,
    filters: filterControls,
    clear,
  } = useListControls(classrooms, {
    searchAccessor: CLASSROOM_SEARCH,
    filters: CLASSROOM_FILTERS,
    sortable: false,
    context: listContext,
  });

  // Default the mentor filter to the logged-in user's own workload (previous
  // behavior of selectedMentorId). Runs once per user; Clear still resets to
  // "All Mentors".
  useEffect(() => {
    if (userData?.user_id) {
      filterControls.mentor?.onChange(String(userData.user_id));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData?.user_id]);

const grouped = groupByKeys(processedClassrooms, [groupByKey]);

  // const rows = [];

  function groupByKeys(array, keys) {
    if (!keys.length) return array;
    const [currentKey, ...restKeys] = keys;
    const grouped = array.reduce((result, item) => {
      const groupKey = item[currentKey] ?? "undefined";
      if (!result[groupKey]) result[groupKey] = [];
      result[groupKey].push(item);
      return result;
    }, {});
    for (const key in grouped) {
      grouped[key] = groupByKeys(grouped[key], restKeys);
    }
    return grouped;
  }

  function renderGroupedTable(groupedData, groupByKey) {
    const rows = [];

    Object.entries(groupedData).forEach(([groupKey, classrooms]) => {
        let label = groupKey;
        if (!groupKey[0] || groupKey[0] === "undefined") {
          label = "(Unspecified)";
        } else if (groupByKey[0] === "teacher_profile_id") {
          label = getTeacherName(parseInt(groupKey, 10));
        } else if (
          groupByKey === "drama_mentor_id" ||
          groupByKey === "art_mentor_id" ||
          groupByKey === "music_mentor_id"
        ) {
          label = getMentorName(parseInt(groupKey, 10));
        }
        let prefix = "";
        if (groupByKey[0] === "district_name") prefix = "District: ";
        else if (groupByKey[0] === "school_name") prefix = "School: ";
        else if (groupByKey[0] === "teacher_profile_id") prefix = "Teacher: ";
        else if (
          groupByKey[0] === "drama_mentor_id" ||
          groupByKey[0] === "art_mentor_id" ||
          groupByKey[0] === "music_mentor_id"
        ) prefix = "Mentor: ";

        rows.push(
          <tr
            key={`group-${groupByKey[0]}-${groupKey}`}
            className={`group-header ${
              groupByKey[0] === "district_name"
                ? "table-primary"
                : groupByKey[0] === "school_name"
                ? "table-warning"
                : groupByKey[0] === "teacher_profile_id"
                ? "table-success"
                : ""
            }`}
          >
            <td colSpan="7"><strong>{prefix}{label}</strong></td>
          </tr>
        );

      // Classroom rows
  // Classroom rows
    classrooms.forEach((classroom) => {
      const isExpanded = expandedRows[classroom.classroom_id];
      const teacher = teacherProfiles.find(t => t.profile_id === classroom.teacher_profile_id);
      const teacherName = teacher ? `${teacher.first_name} ${teacher.last_name}` : '(No Teacher)';
      const visitsSummary = getClassroomEventSummary(classroom.classroom_id, 2);
      const performanceSummary = getClassroomEventSummary(classroom.classroom_id, 1);
      const mentorRows = [
        {
          label: "Drama",
          field: "drama_mentor_id",
          value: classroom.drama_mentor_id,
          mentors: mentors.filter((mentor) => mentor.drama_mentor === true),
        },
        {
          label: "Art",
          field: "art_mentor_id",
          value: classroom.art_mentor_id,
          mentors: mentors.filter((mentor) => mentor.art_mentor === true),
        },
        {
          label: "Music",
          field: "music_mentor_id",
          value: classroom.music_mentor_id,
          mentors: mentors.filter((mentor) => mentor.music_mentor === true),
        },
      ];
      const missingMentorCount = mentorRows.filter((mentor) => !mentor.value).length;

      rows.push(
        <React.Fragment key={`classroom-${classroom.classroom_id}`}>
          {/* Compact row */}
          <tr
            className={`compact-row${isExpanded ? ' is-open' : ''}`}
            onClick={() => toggleRow(classroom.classroom_id)}
            style={{ cursor: 'pointer' }}
          >
            <td colSpan={7}>
              <div className="compact-row-content">
                <div className="compact-main">
                  <div className="compact-title">{teacherName}</div>
                  <div className="compact-subtitle">
                    Classroom Teacher: {classroom.teacher_name || "Not listed"}
                  </div>
                </div>
                <div className="compact-meta">
                  <span className="compact-pill">Grade {getGradeType(classroom.grade_id)}</span>
                  <span className="compact-pill">Size {classroom.size ?? "N/A"}</span>
                  <span className="compact-pill">{getClassroomServiceType(classroom.service_type_id)}</span>
                </div>
                <div className="compact-school">
                  <span>{getSchoolName(classroom.school_id)}</span>
                  <a href={`https://www.google.com/maps/?q=${getSchoolAddress(classroom.school_id)}`}
                      target="_blank"
                      rel="noreferrer">
                    <button type='button' className='btn btn-secondary btn-sm map-pin-btn'><i className="bi bi-geo-alt-fill"></i></button>
                  </a>
                </div>
                <div className="compact-progress">
                <ClassroomStatusCollapsed
                  classroom={classroom}
                  classroomProgress={classroomProgress}
                  classroomEvents={classroomEvents}
                />
                </div>
                <div className="compact-schedule">
                  <small>
                    {`${getScheduleSectionSummary(visitsSummary, "visit")} | Last activity: ${getLastClassroomActivityLabel(classroom.classroom_id, 2)}`}
                  </small>
                  <small>
                    {`${getScheduleSectionSummary(performanceSummary, "performance")} | Last activity: ${getLastClassroomActivityLabel(classroom.classroom_id, 1)}`}
                  </small>
                </div>
                <div className="compact-flags">
                  {!listContext.schoolById.get(classroom.school_id)?.student_body_count && (
                    <span className="badge bg-warning text-dark" title="This school has no student body count recorded, so it contributes 0 students to the C. OBC Performance report. Add the count on the Schools screen.">
                      No student body count
                    </span>
                  )}
                  {missingMentorCount > 0 && (
                    <span className="compact-pill">{missingMentorCount} mentor role(s) unassigned</span>
                  )}
                  {classroom.notes && (
                    <Button
                      variant="danger"
                      size="sm"
                      style={{ color: 'black' }}
                      onClick={(e) => {
                        e.stopPropagation();
                        editNotesClassroom(classroom);
                      }}
                    >
                      <i className="bi bi-pencil"></i> Scheduling Information
                    </Button>
                  )}
                </div>
                <div className="text-muted small">
                  {isExpanded ? '▼ Collapse' : '▶ Expand'}
                </div>
              </div>
            </td>
          </tr>


          {/* Expanded details row */}
          {isExpanded && (
            <tr>
              <td colSpan={7}>
                <div className="classroom-detail-grid p-3 border rounded">
                  <div className="detail-panel">
                    <h6 className="mb-2">Overview</h6>
                    <div className="mb-2">
                      <div>{getTeacherDetails(classroom.teacher_profile_id)}</div>
                      <div><strong>Teacher Status:</strong> {getTeacherStatus(classroom.teacher_profile_id)}</div>
                      <div><strong>School:</strong> {getSchoolName(classroom.school_id)}</div>
                      <div><strong>Address:</strong> {getSchoolAddress(classroom.school_id)}</div>
                      <div><strong>Phone:</strong> {getSchoolPhone(classroom.school_id)}</div>
                    </div>
                    <ClassroomStatus
                      classroom={classroom}
                      classroomProgress={classroomProgress}
                      classroomEvents={classroomEvents}
                    />
                  </div>
                  <div className="detail-panel">
                    <h6 className="mb-2">Mentors</h6>
                    <div className="mentor-chip-list">
                      {mentorRows.map((mentor) => (
                        <div key={`${classroom.classroom_id}-${mentor.label}`} className="mentor-chip-row">
                          <span className="mentor-chip-label">{mentor.label}</span>
                          {isAdmin ? (
                            <div className="mentor-chip-value mentor-inline-dropdown-wrap" onClick={(e) => e.stopPropagation()}>
                              <Dropdown>
                                <Dropdown.Toggle
                                  variant="secondary"
                                  size="sm"
                                  className="mentor-inline-toggle"
                                >
                                  {mentor.value ? (getMentorName(mentor.value) || "Unknown Mentor") : "Unassigned"}
                                </Dropdown.Toggle>
                                <Dropdown.Menu className="mentor-inline-menu">
                                  <Dropdown.Item
                                    onClick={() => updateClassroomMentorInline(classroom, mentor.field, null)}
                                  >
                                    Unassigned
                                  </Dropdown.Item>
                                  {mentor.mentors.map((mentorOption) => (
                                    <Dropdown.Item
                                      key={mentorOption.user_id}
                                      onClick={() =>
                                        updateClassroomMentorInline(
                                          classroom,
                                          mentor.field,
                                          mentorOption.user_id
                                        )
                                      }
                                    >
                                      {mentorOption.first_name} {mentorOption.last_name}
                                    </Dropdown.Item>
                                  ))}
                                </Dropdown.Menu>
                              </Dropdown>
                            </div>
                          ) : (
                            <span className="mentor-chip-value">
                              {mentor.value ? (getMentorName(mentor.value) || "Unknown Mentor") : "Unassigned"}
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                    <div className="mt-3">
                      <strong>Grade:</strong> {getGradeType(classroom.grade_id)}
                      <br />
                      <strong>Service:</strong> {getClassroomServiceType(classroom.service_type_id)}
                    </div>
                  </div>

                  <div className="detail-panel">
                    <h6 className="mb-2">Schedule</h6>
                    <div className="classroom-schedule-stack">
                      {renderClassroomScheduleSection({
                        classroomId: classroom.classroom_id,
                        sectionKey: "visits",
                        title: "Visits",
                        summary: getScheduleSectionSummary(visitsSummary, "visit"),
                        preview: `Last activity: ${getLastClassroomActivityLabel(classroom.classroom_id, 2)}`,
                        children: getEventProgram(classroom),
                      })}
                      {renderClassroomScheduleSection({
                        classroomId: classroom.classroom_id,
                        sectionKey: "performance",
                        title: "Performance",
                        summary: getScheduleSectionSummary(performanceSummary, "performance"),
                        preview: `Last activity: ${getLastClassroomActivityLabel(classroom.classroom_id, 1)}`,
                        children: getPerformanceEvent(classroom.classroom_id),
                      })}
                    </div>
                  </div>

                  <div className="detail-panel actions-panel">
                    <h6 className="mb-2">Actions</h6>
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        editClassroom(classroom);
                      }}
                    >
                      <i className="bi bi-pencil"></i> Edit
                    </Button>
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        setClassroomToDelete(classroom.classroom_id);
                        setShowModal(true);
                      }}
                    >
                      <i className="bi bi-trash3"></i> Delete
                    </Button>
                  </div>
                </div>

              </td>
            </tr>
          )}
        </React.Fragment>
      );
});

    });

    return rows;
  }


  const downloadMentorTeacherReport = async () => {
    if (!selectedSchoolYear) return;
    try {
      const response = await fetch(
        `${backendURL}/api/download_obc_mentor_teacher_assignments/${selectedSchoolYear}`,
        { method: 'GET', credentials: 'include' }
      );
      if (!response.ok) {
        setErrorMessage(`Failed to download report (HTTP ${response.status})`);
        return;
      }
      const yearObj = schoolYearList.find((y) => y.id === parseInt(selectedSchoolYear, 10));
      const yearLabel = yearObj?.school_year ?? selectedSchoolYear;
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `OBC_mentor_teacher_assignments_${yearLabel}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setErrorMessage(`Failed to download report: ${err.message || err}`);
    }
  };

return (
    <div>
      <Button onClick={() => setShowForm(!showForm)}>
        {showForm ? 'Show Classrooms List' : 'Add New Classroom'}
      </Button>
      <div> {/*Select school Year */}
          <Dropdown>
            <span className='fw-bold'>Select School Year</span>
          <select value={selectedSchoolYear} onChange={handleYearChange}>
              <option value="" disabled>Select School Year</option>
              {schoolYearList.map((year) => (
                <option key={year.id} value={year.id}>
                  {year.school_year}
                </option>
              ))}
            </select>
          </Dropdown>
      </div> 
      <div className="filters-row">
        <Form.Group controlId="groupBy">
          <Form.Label><strong>Group By:</strong></Form.Label>
          <Form.Select
            value={groupByKey}
            onChange={(e) => setGroupByKey(e.target.value)}
          >
            <option value="district_name">District</option>
            <option value="school_name">School</option>
            <option value="teacher_profile_id">Teacher</option>
            {/* easy to add more later: mentors, service type, grade, etc. */}
          </Form.Select>
        </Form.Group>

        {/* Both filters live in the shared hook (count/Clear stay accurate).
            The mentor dropdown is only shown to mentor users (old
            hiddenWorkLoad gating; its own-workload default applies either
            way); the student-body data-quality filter shows for everyone. */}
        {filterList.map(({ filterKey, ...f }) =>
          filterKey === 'mentor' && !hiddenWorkLoad ? null : (
            <FilterSelect
              key={filterKey}
              searchable={filterKey === 'mentor'}
              {...f}
            />
          )
        )}

        <SearchInput
          label="Search"
          placeholder="School or teacher name..."
          {...search}
        />

        <ResultSummary
          count={resultCount}
          total={totalCount}
          noun="classroom"
          onClear={clear}
          isFiltered={isFiltered}
        />

        {isAdmin && (
          <Button
            variant="primary"
            className="ms-auto"
            onClick={downloadMentorTeacherReport}
          >
            <i className="bi bi-download"></i> Download Mentor & Teacher Assignments
          </Button>
        )}

      </div>
  
      {showForm ? (
      <Form onSubmit={handleSubmit}>
        <Form.Group controlId="school_id">
            <Form.Label className="bold-label">School</Form.Label>
            <Select
                required
                isSearchable={true}
                options={schoolOptions}
                value={selectedSchool}
                onChange={handleSchoolChange}
                inputId="school_id"
            />
        </Form.Group>
        <Form.Group controlId="school_student_body">
          <Form.Label className="bold-label">School Student Body</Form.Label>
          <Form.Control
            type="number"
            value={schoolStudentBody ?? ''}
            onChange={e => setSchoolStudentBody(e.target.value)}
            placeholder="Total student body for this school"
          />
          {currentClassroom.school_id && isStudentBodyStale(currentClassroom.school_id) && (
            <Form.Text className="text-danger">
              <i className="bi bi-exclamation-triangle"></i> Student body not updated in over a year — please review.
            </Form.Text>
          )}
        </Form.Group>
        <Form.Group controlId="size">
          <Form.Label className="bold-label">Size</Form.Label>
          <Form.Control
          type="number"
          name="size"
          value={currentClassroom.size}
          onChange={handleIntegerChange}
          required
          />

        </Form.Group>

        <Form.Group controlId="grade_id">
                <Form.Label className="bold-label">Grade</Form.Label>
                <Form.Control 
                    required
                    as="select"
                    name="grade_id"
                    onChange={handleGradeChange}
                    value={currentClassroom.grade_id}
                >
                    <option value="">Select Grade</option>
                    {Array.isArray(grades) && grades.map(grade => (
                        <option key={grade.grade_id} value={grade.grade_id}>{grade.grade}</option>
                    ))}
                </Form.Control>
            </Form.Group>
          <Form.Group>
            <Form.Label className="bold-label">Classroom Teacher</Form.Label>
            <Form.Control
              type="text"
              name="teacher_name"
              value={currentClassroom.teacher_name || ""}
              onChange={handleTextChange}
              placeholder="Classroom teacher, not the main contact"
            />
          </Form.Group>

          {/* Teacher Search and Display */}
          <Form.Group controlId="teacher_search">
            <Form.Label className="bold-label">Teacher/Contact</Form.Label>
            <Select 
            required
              options={teacherProfiles.map(teacher => ({
                value: teacher.profile_id,
                label: `${teacher.first_name} ${teacher.last_name}`
              }))}
              value={selectedTeacher ? {
                value: selectedTeacher.profile_id, 
                label: `${selectedTeacher.first_name} ${selectedTeacher.last_name}`
              } : null}
              onChange={(selectedOption) => {
                const teacher = teacherProfiles.find(t => t.profile_id === selectedOption.value);
                handleTeacherSelect(teacher);
              }}
            />
          </Form.Group>

          {/* Drama Mentor Search */}
          {/* <Form.Group controlId="drama_mentor_search">
            <Form.Label className="bold-label">Drama Mentor</Form.Label>
            <Select 
              isClearable
              options={mentors.filter(mentor => mentor.drama_mentor === true).map(mentor => ({
                value: mentor.user_id,
                label: `${mentor.first_name} ${mentor.last_name}`
              }))}
              value={selectedDramaMentor ? {
                value: selectedDramaMentor.user_id, 
                label: `${selectedDramaMentor.first_name} ${selectedDramaMentor.last_name}`
              } : null}
              onChange={(selectedOption) => {
                if (selectedOption) {
                  const mentor = mentors.find(m => m.user_id === selectedOption.value);
                  handleMentorSelect(mentor, 'drama_mentor');
                } else {
                  // Clear selection → remove mentor
                  setSelectedDramaMentor(null);
                  setCurrentClassroom(prev => ({
                    ...prev,
                    drama_mentor_id: null,
                  }));
                }
              }}
            />
          </Form.Group> */}

          {/* Art Mentor Search and Display */}
          {/* <Form.Group controlId="art_mentor_search">
            <Form.Label className="bold-label">Art Mentor</Form.Label>
            <Select 
              isClearable
              options={mentors.filter(mentor => mentor.art_mentor === true).map(mentor => ({
                value: mentor.user_id,
                label: `${mentor.first_name} ${mentor.last_name}`
              }))}
              value={selectedArtMentor ? {
                value: selectedArtMentor.user_id, 
                label: `${selectedArtMentor.first_name} ${selectedArtMentor.last_name}`
              } : null}
              onChange={(selectedOption) => {
                if (selectedOption) {
                  const mentor = mentors.find(m => m.user_id === selectedOption.value);
                  handleMentorSelect(mentor, 'art_mentor');
                } else {
                  // Clear selection → remove mentor
                  setSelectedArtMentor(null);
                  setCurrentClassroom(prev => ({
                    ...prev,
                    art_mentor_id: null,
                  }));
                }
              }}
            />
          </Form.Group> */}


          {/* Music Mentor Search and Display */}
          {/* <Form.Group controlId="music_mentor_search">
            <Form.Label className="bold-label">Music Mentor</Form.Label>
            <Select 
              isClearable
              options={mentors.filter(mentor => mentor.music_mentor === true).map(mentor => ({
                value: mentor.user_id,
                label: `${mentor.first_name} ${mentor.last_name}`
              }))}
              value={selectedMusicMentor ? {
                value: selectedMusicMentor.user_id, 
                label: `${selectedMusicMentor.first_name} ${selectedMusicMentor.last_name}`
              } : null}
              onChange={(selectedOption) => {
                if (selectedOption) {
                  const mentor = mentors.find(m => m.user_id === selectedOption.value);
                  handleMentorSelect(mentor, 'music_mentor');
                } else {
                  // Clear selection → remove mentor
                  setSelectedMusicMentor(null);
                  setCurrentClassroom(prev => ({
                    ...prev,
                    music_mentor_id: null,
                  }));
                }
              }}
            />
          </Form.Group> */}
          <Form.Group>
            <Form.Label className="bold-label">Service Type</Form.Label>
            <Form.Select
            required
            name='service_type_id'
            onChange={handleFormChange}
            value={currentClassroom.service_type_id}
            >
              <option value={''}>Select Service Type</option>
              {classroomServiceTypes.map( service => (
                <option key={service.id} value={service.id}>
                  {service.service_name}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
          <Form.Group>
            <Form.Label className="bold-label">Notes</Form.Label>
            <Form.Control
              as="textarea"
              name="notes"
              value={currentClassroom.notes || ""}
              onChange={handleTextChange}
              rows={4} // Number of visible lines
              placeholder="Add any relevant notes about the classroom..."
              style={{ resize: 'vertical' }} // allows manual resizing (optional)
            />
          </Form.Group>

          <Button type="submit" className="m-1">{ isEditingClassroom ? "Update" : "Submit"}</Button>
          <Button variant="secondary" onClick={handleCancel} className="ml-2">Cancel</Button>
        </Form>
      ) : (
        <div className="scrollable-table">
        <Table striped bordered hover>
          <thead>
            <tr>
              {/* <th>Classroom Info</th>
              <th>Classroom Status</th>
              <th>Classroom Info</th>
              <th>Workshops</th>
              <th>Mentors</th>
              <th>Classroom Visits</th>
              <th>Performance</th>
              <th>Actions</th> */}
            </tr>
          </thead>
          <tbody>
            {renderGroupedTable(grouped, [groupByKey])}
          </tbody>
        </Table>
        </div>
      )}
  
      {/* Delete Confirmation Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Deletion</Modal.Title>
        </Modal.Header>
        <Modal.Body>Are you sure you want to delete this classroom?</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Close
          </Button>
          <Button variant="danger" onClick={deleteClassroom}>
          Delete
          </Button>
        </Modal.Footer>
      </Modal>
        <Modal show={showNewVisitModal} onHide={handleCancel}>
          <Modal.Header>{isEditingVisit? "Editing Visit":"New Visit"}</Modal.Header>
          <Modal.Body>
                  <Form>
                    <Form.Group>
                      <Form.Label>Date</Form.Label>
                      <Form.Control
                      type={'date'}
                      id='date'
                      name='date'
                      value={currentEventDate.date}
                      onChange={handleEventDateFormChange}
                      required>
                      </Form.Control>
                    </Form.Group>
                    <Form.Group>
                      <Form.Label>Start Time</Form.Label>
                      <Form.Control
                      type={'time'}
                      id='start_time'
                      name='start_time'
                      value={currentEventDate.start_time}
                      onChange={handleEventDateFormChange}
                      required></Form.Control>
                    </Form.Group>
                    <Form.Group>
                      <Form.Label>End Time</Form.Label>
                      <Form.Control
                      id='end_time'
                      value={currentEventDate.end_time}
                      type={'time'}
                      name='end_time'
                      onChange={handleEventDateFormChange}
                      required></Form.Control>
                    </Form.Group>
                    <Form.Group>
                      <Form.Label>Select Visit Type</Form.Label>
                      <Form.Select
                        name="rfv_id"
                        value={currentEventDate.rfv_id ?? ''}
                        onChange={(e) => {
                          const val = e.target.value;
                          setCurrentEventDate(prev => ({ 
                            ...prev, 
                            rfv_id: val === '' ? '' : parseInt(val, 10) 
                          }));
                        }}
                        required
                      >
                        <option value=''>Choose...</option>
                        {filteredClassroomEventTypes().map(vt => ( //needs the list of filtered event types here
                          <option key={vt.rfv_id} value={vt.rfv_id}>
                            {vt.reason_for_visit}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                    
                  </Form>
          </Modal.Body>
          <Modal.Footer>
          {currentEventDate.event_dates_id ? 
              <Button variant='danger' onClick={() => deleteEventDate(currentEventDate.event_dates_id)}>Delete</Button>
               : 
               ''}
            <Button onClick={handleVisitSubmit}>{isEditingVisit ? "Update" : "Create"}</Button>
            <Button variant='secondary' onClick={handleCancel}>Cancel</Button>
          </Modal.Footer>
        </Modal>
        <Modal show={showNewPerformanceModal} onHide={handleCancel}>
          <Modal.Header>{isEditingPerformance ? "Editing Performance":"New Performance" }</Modal.Header>
          <Modal.Body>
                  <Form>
                    <Form.Group>
                      <Form.Label>Date</Form.Label>
                      <Form.Control
                      type={'date'}
                      id='date'
                      name='date'
                      value={currentPerformanceDate.date}
                      onChange={handlePerformanceDateFormChange}
                      required>
                      </Form.Control>
                    </Form.Group>
                    <Form.Group>
                      <Form.Label>Start Time</Form.Label>
                      <Form.Control
                      type={'time'}
                      id='start_time'
                      name='start_time'
                      value={currentPerformanceDate.start_time}
                      onChange={handlePerformanceDateFormChange}
                      required></Form.Control>
                    </Form.Group>
                    <Form.Group>
                      <Form.Label>End Time</Form.Label>
                      <Form.Control
                      id='end_time'
                      value={currentPerformanceDate.end_time}
                      type={'time'}
                      name='end_time'
                      onChange={handlePerformanceDateFormChange}
                      required></Form.Control>
                    </Form.Group>                    
                  </Form>
          </Modal.Body>
          <Modal.Footer>
          {currentPerformanceDate.event_dates_id ? 
              <Button variant='danger' onClick={() => deletePerformanceDate(currentPerformanceDate.event_dates_id)}>Delete</Button>
               : 
               ''}
            <Button onClick={handlePerformanceSubmit}>{isEditingPerformance ? "Update" : "Create"}</Button>
            <Button variant='secondary' onClick={handleCancel}>Cancel</Button>
          </Modal.Footer>
        </Modal>
        <Modal show={isEditingNotesClassroom} onHide={handleCancel}>
          <Modal.Header>Scheduling Information</Modal.Header>
          <Modal.Body>{currentClassroom.notes}</Modal.Body>
          <Modal.Footer></Modal.Footer>
        </Modal>
        <ErrorModalMessage errorMessage={errorMessage} clearErrorMessage={() => setErrorMessage('')} />
    </div>
  );
}
export default ClassroomComponent;
